import React, { memo, useCallback, useMemo } from "react";
import { Heading, Button } from "../../components";

const NotFoundErrorComponent = ({
  bodyClass,
  flexBodyClass,
  view,
  workflowID,
  matchId,
  navigate,
  error,
}) => {
  const id = !view ? matchId : workflowID;

  const handleNavigation = useCallback(() => {
    navigate(`/search`, { replace: true });
  }, [navigate]);

  const normalizeError = (rawError) => {
    if (!rawError) return null;

    let parsedError = rawError;

    if (typeof rawError === "string") {
      try {
        parsedError = JSON.parse(rawError);
      } catch (e) {
        // It's just a regular string error message, not JSON
        return { status: null, message: rawError };
      }
    }

    return parsedError;
  };

  const errorState = useMemo(() => {
    const errObj = normalizeError(error);

    const result = {
      title: "Unexpected Error",
      message: "An unknown error occurred.",
      status: 500,
    };

    if (!errObj) return result;

    const status = errObj.status || errObj.response?.status;
    let rawMessage =
      errObj.message || errObj.error || "An unexpected error occurred.";

    if (typeof rawMessage === "object") {
      try {
        rawMessage = JSON.stringify(rawMessage);
      } catch (e) {
        rawMessage = "Error details could not be rendered.";
      }
    }

    result.status = status;

    // Logic for specific status codes
    if (status === 404 || String(rawMessage).includes("No such workflow")) {
      result.title = "Workflow Not Found";
      result.message = `No workflow found with ID: ${id}. It may have been deleted or expired.`;
    } else if (status === 500) {
      result.title = "Internal Server Error";
      // We can show the technical message if needed, or a generic one.
      // Using the technical message allows you to see "HikariConfig" issues if necessary.
      result.message =
        rawMessage || "A server error occurred. Please try again later.";
    } else {
      result.title = "Error";
      result.message = String(rawMessage);
    }

    return result;
  }, [error, id]);

  // Styles for the card look
  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60vh",
      backgroundColor: "#f9f9f9",
    },
    card: {
      backgroundColor: "#ffffff",
      padding: "40px",
      borderRadius: "12px",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
      maxWidth: "600px",
      width: "100%",
      textAlign: "center",
      border: "1px solid #e0e0e0",
    },
    iconContainer: {
      marginBottom: "20px",
    },
    message: {
      marginBottom: "30px",
      color: "#666",
      lineHeight: "1.5",
      fontSize: "16px",
      wordWrap: "break-word",
    },
  };

  return (
    <div className={`${bodyClass} ${flexBodyClass}`} style={styles.container}>
      <div style={styles.card}>
        {/* Error Icon SVG */}
        <div style={styles.iconContainer}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="#D32F2F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8V12"
              stroke="#D32F2F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 16H12.01"
              stroke="#D32F2F"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <Heading level={2} style={{ marginBottom: "10px", color: "#111" }}>
          {errorState.title}
        </Heading>

        <div style={styles.message}>{errorState.message}</div>

        {!view && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleNavigation}
            style={{ padding: "10px 24px", fontWeight: "bold" }}
          >
            Go to Search
          </Button>
        )}
      </div>
    </div>
  );
};

const NotFoundError = memo(NotFoundErrorComponent);
NotFoundError.displayName = "NotFoundError";

export default NotFoundError;
